import React, { useMemo } from "react";
import { Marker, Tooltip, Polyline, CircleMarker } from "react-leaflet";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import {
  selectViewOptions,
  selectTriangulationSettings,
} from "../../store/userSettingsSlice";
import { IDevice } from "../../types";
import { TooltipPosition } from "../../utils/tooltipPositioning";
import L from "leaflet";

interface DeviceMarkerProps {
  device: IDevice;
  showName?: boolean;
  showRSSI?: boolean;
  tooltipPosition?: TooltipPosition;
}

// Create custom device icon (circle)
const createDeviceIcon = (
  device: IDevice,
  isOnline: boolean,
  isSelected: boolean
) => {
  // Inner color: custom color or status-based fallback
  let innerColor =
    device.customSettings?.color || (isOnline ? "#10b981" : "#ef4444");

  let size = 22;
  let borderColor = "white";
  let borderWidth = 4;

  // Border based on triangulation confidence
  const confidence = device.triangulationInfo?.confidence;
  if (confidence !== undefined) {
    if (confidence > 0.94) {
      borderColor = "#dddddd"; // green for high confidence
      borderWidth = 2;
      // borderColor = "#06cf06ff"; // green for high confidence
      // borderWidth = 3;
    } else if (confidence > 0.8) {
      borderColor = "#dddddd"; // yellow/orange for medium
      borderWidth = 2;
      // borderColor = "#79beffff"; // yellow/orange for medium
      // borderWidth = 4;
    } else if (confidence > 0.5) {
      borderColor = "#dddddd"; // yellow/orange for medium
      borderWidth = 2;
      // borderColor = "#f59e0b"; // yellow/orange for medium
      // borderWidth = 4;
    } else {
      borderColor = "#dddddd"; // red for low
      borderWidth = 2;
      // borderColor = "#ef4444"; // red for low
      // borderWidth = 5;
    }
  } else {
    borderColor = "#9ca3af"; // gray for no triangulation info
    borderWidth = 4;
  }

  // Highlight selected devices: override inner color to blue, keep confidence-based border
  if (isSelected) {
    innerColor = "#0011ffff"; // blue for selected
    borderColor = "#ffffffff"; // yellow/orange for medium
    size = 28; // larger size
    // Border remains confidence-based, but ensure it's visible
    if (borderColor === "white") borderColor = "#1d4ed8"; // darker blue if was white
  }

  return L.divIcon({
    html: `
      <div style="
        width: ${size}px;
        height: ${size}px;
        background-color: ${innerColor};
        border: ${borderWidth}px solid ${borderColor};
        border-radius: 50%;
        box-shadow: ${
          isSelected
            ? "0 0 20px 8px rgba(0, 17, 255, 0.6), 0 0 40px 15px rgba(0, 17, 255, 0.4), 0 2px 4px rgba(0,0,0,0.3)"
            : "0 2px 4px rgba(0,0,0,0.3)"
        };
        ${isSelected ? "animation: pulse 2s infinite;" : ""}
      "></div>
      ${
        isSelected
          ? `
        <style>
          @keyframes pulse {
            0% { 
              transform: scale(1); 
              opacity: 1;
              box-shadow: 0 0 20px 3px rgba(255, 255, 255, 0.6), 0 0 40px 12px rgba(255, 255, 216, 0.4), 0 2px 4px rgba(0,0,0,0.3);
            }
            50% { 
              transform: scale(1.2); 
              opacity: 0.9;
              box-shadow: 0 0 30px 9px rgba(255, 255, 255, 0.8), 0 0 60px 17px rgba(255, 255, 255, 0.5), 0 2px 4px rgba(0,0,0,0.3);
            }
            100% { 
              transform: scale(1); 
              opacity: 1;
              box-shadow: 0 0 20px 3px rgba(255, 255, 255, 0.6), 0 0 40px 12px rgba(255, 255, 255, 0.4), 0 2px 4px rgba(0,0,0,0.3);
            }
          }
        </style>
      `
          : ""
      }
    `,
    className: "device-marker",
    iconSize: [size + borderWidth * 2, size + borderWidth * 2],
    iconAnchor: [(size + borderWidth * 2) / 2, (size + borderWidth * 2) / 2],
  });
};

// Create custom device name box icon (rectangle)
const createDeviceNameBoxIcon = (
  device: IDevice,
  isOnline: boolean,
  isSelected: boolean
) => {
  // Get device name
  const deviceName =
    device.customSettings?.displayName || device.name || device.mac;

  // Calculate width based on text length (approximate)
  const charWidth = 8; // approximate pixels per character
  const padding = 6; // horizontal padding
  const minWidth = 20;
  const maxWidth = 200;
  const calculatedWidth = Math.min(
    Math.max(deviceName.length * charWidth + padding, minWidth),
    maxWidth
  );

  const height = 24;
  const borderWidth = 2;
  const borderColor = isSelected ? "#ffffff" : "#1d4ed8"; // blue border
  const backgroundColor = isSelected ? "#1d4ed8" : "#ffffff"; // white background
  const textColor = isSelected ? "#ffffff" : "#1f2937"; // dark gray text

  return L.divIcon({
    html: `
      <div style="
        min-width: ${minWidth}px;
        max-width: ${maxWidth}px;
        width: auto;
        height: ${height}px;
        background-color: ${backgroundColor};
        border: ${borderWidth}px solid ${borderColor};
        border-radius: 4px;
        box-shadow: ${
          isSelected
            ? "0 0 15px 4px rgba(37, 99, 235, 0.5), 0 2px 4px rgba(0,0,0,0.2)"
            : "0 2px 4px rgba(0,0,0,0.2)"
        };
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0 8px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-size: 12px;
        font-weight: 500;
        color: ${textColor};
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        ${isSelected ? "animation: pulse-box 2s infinite;" : ""}
      ">${deviceName}</div>
      ${
        isSelected
          ? `
        <style>
          @keyframes pulse-box {
            0% { 
              transform: scale(1); 
              opacity: 1;
              box-shadow: 0 0 15px 4px rgba(37, 99, 235, 0.5), 0 2px 4px rgba(0,0,0,0.2);
            }
            50% { 
              transform: scale(1.05); 
              opacity: 0.95;
              box-shadow: 0 0 20px 6px rgba(37, 99, 235, 0.7), 0 2px 4px rgba(0,0,0,0.2);
            }
            100% { 
              transform: scale(1); 
              opacity: 1;
              box-shadow: 0 0 15px 4px rgba(37, 99, 235, 0.5), 0 2px 4px rgba(0,0,0,0.2);
            }
          }
        </style>
      `
          : ""
      }
    `,
    className: "device-name-box-marker",
    iconSize: [calculatedWidth, height + borderWidth * 2],
    iconAnchor: [calculatedWidth / 2, (height + borderWidth * 2) / 2],
  });
};

const DeviceMarker: React.FC<DeviceMarkerProps> = React.memo(
  ({ device, showName = true, showRSSI = true, tooltipPosition }) => {
    const { language, selectedDeviceMac } = useSelector(
      (state: RootState) => state.ui
    );

    // Get view options from user settings
    const viewOptions = useSelector(selectViewOptions);
    const {
      showLastSeen = false,
      showConfidence = false,
      showDeviceAsNameBox = false,
    } = viewOptions || {};

    // Get triangulation settings for debug info
    const triangulationSettings = useSelector(selectTriangulationSettings);
    const showTriangulationDebug =
      triangulationSettings?.showDebugInfo || false;

    // Check if device is online (received data in last 3 minutes)
    const isOnline = useMemo(
      () =>
        device.lastSeen
          ? Date.now() - new Date(device.lastSeen).getTime() < 180000
          : false,
      [device.lastSeen]
    );

    // Check if this device is selected
    const isSelected = selectedDeviceMac === device.mac;

    // Use pixel coordinates directly with L.CRS.Simple
    // In L.CRS.Simple, coordinates are [y, x] (lat, lng equivalent)
    const deviceLatLng: [number, number] = useMemo(
      () => (device.position ? [device.position.y, device.position.x] : [0, 0]),
      [device.position?.x, device.position?.y]
    );

    // Consistent name resolution: prioritize displayName, then API name, then MAC
    const deviceName = useMemo(
      () => device.customSettings?.displayName || device.name || device.mac,
      [device.customSettings?.displayName, device.name, device.mac]
    );

    const lastSeenText = useMemo(
      () =>
        device.lastSeen
          ? new Date(device.lastSeen).toLocaleTimeString()
          : language === "he"
          ? "לא ידוע"
          : "Unknown",
      [device.lastSeen, language]
    );

    // Check if there's any content to display in the tooltip
    const hasTooltipContent = useMemo(
      () =>
        (showName && deviceName) ||
        (showRSSI && device.rssi) ||
        showLastSeen ||
        (showConfidence && device.triangulationInfo) ||
        (showTriangulationDebug && device.triangulationInfo),
      [
        showName,
        deviceName,
        showRSSI,
        device.rssi,
        showLastSeen,
        showConfidence,
        showTriangulationDebug,
        device.triangulationInfo,
      ]
    );

    // Map diagonal directions to standard Leaflet directions for the tooltip component
    const getLeafletDirection = (
      direction?: string
    ): "top" | "bottom" | "left" | "right" => {
      if (!direction) return "top";

      // Map diagonal directions to their primary direction
      if (direction.includes("top")) return "top";
      if (direction.includes("bottom")) return "bottom";
      if (direction.includes("left")) return "left";
      if (direction.includes("right")) return "right";

      return direction as "top" | "bottom" | "left" | "right";
    };

    const leafletDirection = useMemo(
      () => getLeafletDirection(tooltipPosition?.direction),
      [tooltipPosition?.direction]
    );

    // Memoize the device icon to prevent recreation on every render
    const deviceIcon = useMemo(
      () =>
        showDeviceAsNameBox
          ? createDeviceNameBoxIcon(device, isOnline, isSelected)
          : createDeviceIcon(device, isOnline, isSelected),
      [
        showDeviceAsNameBox,
        device.mac,
        device.customSettings?.displayName,
        device.name,
        device.customSettings?.color,
        device.triangulationInfo?.confidence,
        isOnline,
        isSelected,
      ]
    );

    // Use displayPosition if available (from collision detection), otherwise use original position
    const displayPosition = useMemo(
      () => (device as any).displayPosition || device.position,
      [(device as any).displayPosition, device.position]
    );

    const displayLatLng: [number, number] = useMemo(
      () => [displayPosition.y, displayPosition.x],
      [displayPosition.x, displayPosition.y]
    );

    const needsLeaderLine = useMemo(
      () => (device as any).needsLeaderLine && showDeviceAsNameBox,
      [(device as any).needsLeaderLine, showDeviceAsNameBox]
    );

    // Memoize tooltip content to prevent unnecessary recalculations
    const tooltipContent = useMemo(
      () => (
        <div className="text-xs">
          {showName && <div className="font-semibold">{deviceName}</div>}
          {showRSSI && device.rssi && (
            <div className="text-gray-600">RSSI: {device.rssi}dBm</div>
          )}
          {showLastSeen && (
            <div className="text-gray-500">
              {language === "he" ? "נראה לאחרונה" : "Last seen"}: {lastSeenText}
            </div>
          )}
          {showConfidence && device.triangulationInfo && (
            <div className="text-gray-600">
              {language === "he" ? "ביטחון" : "Confidence"}:{" "}
              <span
                className={
                  device.triangulationInfo.confidence > 0.8
                    ? "text-green-800"
                    : device.triangulationInfo.confidence > 0.5
                    ? "text-yellow-800"
                    : "text-red-800"
                }
              >
                {Math.round(device.triangulationInfo.confidence * 100)}%
              </span>
            </div>
          )}
          {showTriangulationDebug && device.triangulationInfo && (
            <div className="mt-2 pt-2 border-t border-gray-300 text-xs">
              <div className="text-blue-600 font-semibold mb-1">
                {language === "he" ? "מידע טריאנגולציה" : "Triangulation Info"}
              </div>
              <div className="text-gray-600">
                <div>
                  Method:{" "}
                  <span className="text-blue-500">
                    {device.triangulationInfo.method}
                  </span>
                </div>
                <div>Confidence: </div>
                <div>
                  Signals:{" "}
                  <span className="text-purple-600">
                    {device.triangulationInfo.signalsUsed}
                  </span>
                </div>
                {device.triangulationInfo.primaryGateway && (
                  <div>
                    Gateway:{" "}
                    <span className="text-orange-600">
                      {device.triangulationInfo.primaryGateway}
                    </span>
                  </div>
                )}
                <div className="text-xs text-gray-400 mt-1">
                  {new Date(
                    device.triangulationInfo.lastCalculated
                  ).toLocaleTimeString()}
                </div>
              </div>
            </div>
          )}
        </div>
      ),
      [
        showName,
        deviceName,
        showRSSI,
        device.rssi,
        showLastSeen,
        lastSeenText,
        showConfidence,
        showTriangulationDebug,
        device.triangulationInfo,
        language,
      ]
    );

    // Validate device position - do this after all hooks to comply with Rules of Hooks
    if (
      !device.position ||
      typeof device.position.x !== "number" ||
      typeof device.position.y !== "number" ||
      isNaN(device.position.x) ||
      isNaN(device.position.y)
    ) {
      return null;
    }

    return (
      <>
        {/* Leader line from original position to displaced name box using Leaflet components */}
        {needsLeaderLine && (
          <>
            <Polyline
              positions={[deviceLatLng, displayLatLng]}
              pathOptions={{
                color: "#f1f8ffff",
                weight: 6,
                opacity: 0.9,
                dashArray: "3, 3",
              }}
            />
            <CircleMarker
              center={deviceLatLng}
              radius={8}
              pathOptions={{
                fillColor: "#f7faffff",
                fillOpacity: 0.7,
                color: "#2563eb",
                weight: 2,
              }}
            />
          </>
        )}
        <Marker position={displayLatLng} icon={deviceIcon}>
          {hasTooltipContent && (
            <Tooltip
              permanent={hasTooltipContent}
              direction={leafletDirection}
              offset={tooltipPosition?.offset || [0, -10]}
              className={`device-tooltip tooltip-${leafletDirection}`}
            >
              {tooltipContent}
            </Tooltip>
          )}
        </Marker>
      </>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison function for React.memo
    // Only re-render if these specific properties change
    return (
      prevProps.device.mac === nextProps.device.mac &&
      prevProps.device.position?.x === nextProps.device.position?.x &&
      prevProps.device.position?.y === nextProps.device.position?.y &&
      prevProps.device.rssi === nextProps.device.rssi &&
      prevProps.device.lastSeen === nextProps.device.lastSeen &&
      prevProps.device.customSettings?.displayName ===
        nextProps.device.customSettings?.displayName &&
      prevProps.device.customSettings?.color ===
        nextProps.device.customSettings?.color &&
      prevProps.device.triangulationInfo?.confidence ===
        nextProps.device.triangulationInfo?.confidence &&
      prevProps.showName === nextProps.showName &&
      prevProps.showRSSI === nextProps.showRSSI &&
      prevProps.tooltipPosition?.direction ===
        nextProps.tooltipPosition?.direction &&
      JSON.stringify(prevProps.tooltipPosition?.offset) ===
        JSON.stringify(nextProps.tooltipPosition?.offset)
    );
  }
);

export default DeviceMarker;
